const subjectSelectionService = {
    getSections(packageProduct) {
        return (packageProduct?.sections || [])
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    },
    isIBPackage(packageProduct) {
        return packageProduct?.name?.includes("IB");
    },
    isSubjectHigher(currentSubject) {
        return currentSubject?.name?.includes("Higher");
    },
    getSubjects(packageProduct) {
        return (packageProduct?.sections || []).flatMap((x) => x.subjects);
    },
    getHigherSubjectsToSelect(packageProduct, selectedSubjectsIds = []) {
        const subjects = this.getSubjects(packageProduct);
        const selectedSubjects = subjects.filter((s) => selectedSubjectsIds.includes(s.productId));

        const foundByHigher = subjects.filter((s) => (selectedSubjectsIds || [])
            .find((sId) => sId === s.higherSubject?.productId));
        const foundByHigherIds = foundByHigher.map((s) => s.productId);
        const standardWithHigherSubjects = (selectedSubjects || [])
            .filter((subject) => !!subject.higherSubject)
            .filter((subject) => !foundByHigherIds.includes(subject.productId));
        const allHigherOptions = [...foundByHigher, ...standardWithHigherSubjects];

        return allHigherOptions.toSorted((a, b) => a.name.localeCompare(b.name));
    },
    getLockedSubjects(packageProduct) {
        return (packageProduct?.sections || [])
            .flatMap((x) => x.subjects)
            .filter((x) => x.isLocked);
    },
    getSelectedSubjects(packageProduct, selectedSubjectsIds = []) {
        return (packageProduct?.sections || [])
            .flatMap((section) => section.subjects.map((s) => ({ ...s, sectionName: section.name })))
            .filter((s) => selectedSubjectsIds.includes(s.productId));
    },
    getSubjectVariant(currentSubject, packageProduct, selectedSubjectsIds, isHigherSelection) {
        const { absoluteMaxSubjects, maxHigherSubjects } = packageProduct || {};

        const isPackageIB = this.isIBPackage(packageProduct);
        const hasCurrentSubjectHigher = !!currentSubject.higherSubject;
        const isSubjectHigher = this.isSubjectHigher(currentSubject);

        const subjects = this.getSubjects(packageProduct);
        const selectedSubjects = subjects.filter((s) => (selectedSubjectsIds || []).includes(s.productId));

        const selectedSubjectSlots = selectedSubjects.reduce((acc, s) => acc + (s.numberOfSlots || 1), 0);
        const leftSlots = absoluteMaxSubjects ? absoluteMaxSubjects - selectedSubjectSlots : undefined;
        const isSelectionDisabled = isPackageIB && isSubjectHigher ? false : selectedSubjectSlots === absoluteMaxSubjects;
        const subjectsIncompatible = selectedSubjects.flatMap((s) => s.incompatibleSubjectProductIds || []);

        const foundStandardSubject = subjects.find((item) => item?.productId === currentSubject.productId);
        const isHigherSubjectSelected = isHigherSelection ? false : foundStandardSubject?.higherSubject && !!(selectedSubjectsIds || []).includes(foundStandardSubject?.higherSubject?.productId);

        const foundByHigher = subjects.filter((s) => (selectedSubjectsIds || []).find((sId) => sId === s.higherSubject?.productId));
        const isSelectionHigherDisabled = isHigherSelection ? (hasCurrentSubjectHigher ? false : foundByHigher.length === maxHigherSubjects) : false;

        const selectedSection = (packageProduct.sections || [])
            .find((section) => section.subjects
                .some((subject) => subject.productId === currentSubject.productId || subject.higherSubject?.productId === currentSubject.productId));
        const foundSubjectsInSection = selectedSection.subjects
            .filter((subject) => {
                const foundSubject = (selectedSubjectsIds || []).includes(subject.productId);
                const foundHigherSubject = (selectedSubjectsIds || []).includes(subject.higherSubject?.productId);

                return foundSubject || foundHigherSubject;
            })
            .reduce((acc, item) => acc + (item.numberOfSlots || 1), 0);
        const isSectionDisabled = isHigherSelection ? false : selectedSection.maxSubjects && foundSubjectsInSection >= selectedSection.maxSubjects;

        const numberOfSlotsCheck = isPackageIB ? false : (leftSlots && currentSubject.numberOfSlots && currentSubject.numberOfSlots > leftSlots);

        return {
            isLocked: currentSubject.isLocked,
            isSelected: selectedSubjectsIds.includes(currentSubject.productId)
                || selectedSubjects?.find((x) => x.productId === currentSubject.productId)
                || isHigherSubjectSelected,
            isDisabled: numberOfSlotsCheck
                || subjectsIncompatible.includes(currentSubject.productId)
                || isSelectionDisabled
                || isSectionDisabled
                || isSelectionHigherDisabled,
        };
    },
    checkLimits(packageProduct, selectedSubjectsIds = []) {
        const {
            absoluteMaxSubjects,
            includedMaxSubjects,
            recommendedMaxSubjects,
        } = packageProduct || {};
        const subjects = this.getSubjects(packageProduct);
        const selectedSubjects = subjects.filter((s) => selectedSubjectsIds.includes(s.productId));

        const selectedSubjectSlots = selectedSubjects.reduce((acc, s) => acc + (s.numberOfSlots || 1), 0);

        const hasAbsoluteMaxSubjects = absoluteMaxSubjects === selectedSubjectSlots;
        const hasIncludedMaxSubjects = includedMaxSubjects === selectedSubjectSlots;
        const hasRecommendedMaxSubjects = recommendedMaxSubjects === selectedSubjectSlots;
        return {
            hasAbsoluteMaxSubjects,
            hasIncludedMaxSubjects,
            hasRecommendedMaxSubjects,
        };
    },
    checkIfRequiredSelected(packageProduct, selectedSubjectsIds = []) {
        const { requiredNumberOfSubjects } = packageProduct || {};

        const subjects = this.getSubjects(packageProduct);
        const selectedSubjects = subjects.filter((s) => selectedSubjectsIds.includes(s.productId));
        const selectedSlots = (selectedSubjects || []).reduce((acc, s) => acc + (s.numberOfSlots || 1), 0);

        const areRequiredSelected = selectedSlots >= (requiredNumberOfSubjects || 0);
        const areRequiredSectionsSelected = (packageProduct?.sections || [])
            .filter((section) => !!section.minSubjects)
            .every((section) => {
                const foundSubjectsInSection = section.subjects
                    .filter((subject) => selectedSubjectsIds
                        ?.find((sId) => sId === subject.productId || sId === subject.higherSubject?.productId))
                    .reduce((acc, item) => acc + (item.numberOfSlots || 1), 0);

                return foundSubjectsInSection >= (section.minSubjects || 0);
            });

        return areRequiredSelected && areRequiredSectionsSelected;
    },
    hasHigherSubjects(packageProduct, selectedSubjectsIds = []) {
        const subjects = this.getSubjects(packageProduct);
        const selectedSubjects = (subjects || []).filter((s) => selectedSubjectsIds.includes(s.productId));

        return (selectedSubjects || []).some((subject) => !!subject.higherSubject);
    },
};

export default subjectSelectionService;
